import React, { useState, useEffect, useCallback, useRef } from 'react';
import BasicDrawer from '../../../components/Drawer';
import { Controller, useForm } from 'react-hook-form';
import {
    Button,
    TextField,
    PaginatedSelect,
    buildPropertyOption,
} from '../../../components';
import LabelWithSelect from '../../../components/LabelWithSelect';
import { ApiCaller, Colors, Utils } from '../../../config';
import { Col, ConfigProvider, DatePicker, Row } from 'antd';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import RangeDatePicker from '../../../components/Picker/RangePicker';
import { TimePicker } from 'antd';
import { useMutation, useQuery } from 'react-query';
import {
    getAllUsers,
    getEligibiity,
    getPropertiesPage,
    getPropertyDetail,
} from '../../../config/getApis';
import LabelWithField from '../../../components/LabelWithField';
import {
    AssignNormalShiftSchema,
    AssignOpenShiftSchema,
} from '../../../config/Schema';
import { useDispatch } from 'react-redux';
import { ShiftSchedulerAction } from '../../../store/actions';
import avatar from '../../../assets/images/avatar.jpg';
import useColors from '../../../hooks/useColors';
import { shiftBreaks } from '../../../config/variables';

const AssignShiftDrawer = ({
    drawerStates,
    setDrawerStates,
    shiftState,
    setShiftState,
    openShiftDetail,
}) => {
    const { colorPrimary } = useColors();

    const { data: usersOptions } = useQuery('users', getAllUsers, {
        enabled: drawerStates.typeOfShift == 'normal',
    });
    // Properties are loaded one page at a time by the dropdown itself (search +
    // infinite scroll), so we cache the raw objects we have seen to derive
    // addresses/locations for whichever one gets selected. Options themselves
    // come from the shared builder so every screen renders them the same way.
    const propertyCacheRef = useRef({});

    const toPropertyOption = (p) => {
        propertyCacheRef.current[p.id] = p;
        return buildPropertyOption(p);
    };

    // One page of properties for the dropdown, honouring the typed search term
    const fetchPropertiesPage = useCallback(async ({ page, search }) => {
        const { data, hasMore } = await getPropertiesPage({ page, search });
        return { options: data.map(toPropertyOption), hasMore };
    }, []);

    // Track the currently selected property object
    const [selectedProperty, setSelectedProperty] = useState(null);

    // Keeps the selected property visible even when it is not on a loaded page
    const selectedPropertyOption = selectedProperty
        ? [toPropertyOption(selectedProperty)]
        : [];

    // Resolves a property id to its full object (cache first, API otherwise)
    const resolveProperty = async (id) => {
        if (!id) return null;
        const cached = propertyCacheRef.current[id];
        if (cached?.addresses || cached?.locations) return cached;
        const detail = await getPropertyDetail(id);
        if (detail) propertyCacheRef.current[id] = detail;
        return detail || cached || null;
    };

    // Address options derived from selected property
    const addressOptions = (selectedProperty?.addresses || []).map((addr) => ({
        label: [
            addr.streetNumber,
            addr.streetName,
            addr.city,
            addr.state,
            addr.zip,
        ]
            .filter(Boolean)
            .join(', '),
        value: addr.id,
    }));

    // Location options derived from selected property
    const locationOptions = (selectedProperty?.locations || []).map((loc) => ({
        label: loc.name,
        value: loc.id,
    }));
    const { data: elegibilityOptions } = useQuery(
        'eligibilites',
        getEligibiity,
        {
            enabled:
                drawerStates.typeOfShift == 'normal' ||
                drawerStates.typeOfShift == 'open',
        },
    );

    const schema =
        drawerStates.typeOfShift == 'open'
            ? AssignOpenShiftSchema
            : AssignNormalShiftSchema;

    const defaultOpenShiftValues = {
        // break: '',
        numberOfAssignees: '',
        propertyId: '',
        eligibilityId: '',
        recurringFrequency: '',
        notes: '',
        addressId: '',
        locationId: '',
        assignTo: '',
    };
    const defaultNormalShiftValues = {
        // break: '',
        propertyId: '',
        eligibilityId: '',
        notes: '',
        addressId: '',
        locationId: '',
        assignTo: '',
    };
    const { handleSubmit, control, reset, setValue } = useForm({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues:
            drawerStates.typeOfShift == 'open'
                ? defaultOpenShiftValues
                : defaultNormalShiftValues,
    });

    const dispatch = useDispatch();

    // for time and date only, we are making them controlled component like this, rest will be handle by useform.
    const [state, setState] = useState({
        startTime: '',
        endTime: '',
        startDate: '',
        endDate: '',
        shiftDate: '',
    });

    // validations only for time and date, rest will be handle by the useForm and yup.
    const [validation, setValidations] = useState({
        startTime: '',
        endTime: '',
        startDate: '',
        endDate: '',
        shiftDate: '',
    });

    const [currentUserOption, setCurrentUserOption] = useState([]);

    // Retrieve the current user from local storage
    useEffect(() => {
        const user = localStorage.getItem('user');
        if (user) {
            try {
                const parsedUser = JSON.parse(user);
                const userId = parseduser?.id; // Get the user ID
                const userName =
                    `${parseduser?.firstName} ${parseduser?.lastName}` ||
                    'Current User';
                if (userId) {
                    setCurrentUserOption([{ value: userId, label: userName }]);
                }
            } catch (error) {
                console.error(
                    'Failed to parse user data from localStorage:',
                    error,
                );
            }
        }
    }, []);

    // normal shift creation
    const normalShiftMutation = useMutation(
        async (data) => {
            let users = [];
            let assign = data?.assignTo;
            users.push(assign);
            data.assignTo = users;
            data.isOpen = false;
            if (
                data?.numberOfAssignees == '' ||
                data?.recurringFrequency ||
                data?.startDate == ''
            ) {
                let {
                    numberOfAssignees,
                    startDate,
                    recurringFrequency,
                    endDate,
                    ...restData
                } = data;
                let resp = await ApiCaller.Post('/shift', restData);
                return resp;
            } else {
                let resp = await ApiCaller.Post('/shift', data);
                return resp;
            }
        },
        {
            onSuccess: (resp) => {
                const { schedule, user, eligiblity } = shiftState?.filterDrawer;
                const { tabKey } = shiftState;
                Utils.showMessage('success', resp?.data?.message);
                setDrawerStates((prev) => ({
                    ...prev,
                    shiftTypeDrawer: false,
                    typeOfShift: '',
                }));
                setShiftState((prev) => ({
                    ...prev,
                    page: shiftState?.page,
                    tabKey: tabKey,
                    filterDrawer: {
                        schedule: schedule,
                        user: user,
                        eligiblity: eligiblity,
                    },
                }));
                reset(
                    drawerStates.typeOfShift == 'open'
                        ? defaultOpenShiftValues
                        : defaultNormalShiftValues,
                );
                setValue('startTime', '');
                setValue('endTime', '');
                setValue('startDate', '');
                setValue('endDate', '');
                setValue('shiftDate', '');
                setState({
                    startTime: '',
                    endTime: '',
                    startDate: '',
                    endDate: '',
                    shiftDate: '',
                });
                dispatch(
                    ShiftSchedulerAction.GetShifts({
                        limit: 5,
                        page: 1,
                        tabKey: tabKey,
                    }),
                );
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                return;
            },
            onError: (err) => {
                Utils.showMessage('error', err?.response?.data?.message);
            },
        },
    );

    // open shift creation
    const openShiftMutation = useMutation(
        async (data) => {
            let { assignTo, ...restData } = data;
            restData.isRecurring = true;
            restData.isOpen = true;

            if (restData?.shiftDate == '') {
                let { shiftDate, ...restOpenData } = restData;
                let resp = await ApiCaller.Post('/shift', restOpenData);
                return resp;
            } else {
                let resp = await ApiCaller.Post('/shift', restData);
                return resp;
            }
        },
        {
            onSuccess: (resp) => {
                const { schedule, user, eligiblity } = shiftState?.filterDrawer;
                const { tabKey } = shiftState;
                Utils.showMessage('success', resp?.data?.message);
                setDrawerStates((prev) => ({
                    ...prev,
                    shiftTypeDrawer: false,
                    typeOfShift: '',
                }));
                setShiftState((prev) => ({
                    ...prev,
                    page: shiftState?.page,
                    tabKey: tabKey,
                    filterDrawer: {
                        schedule: schedule,
                        user: user,
                        eligiblity: eligiblity,
                    },
                }));
                reset(
                    drawerStates.typeOfShift == 'open'
                        ? defaultOpenShiftValues
                        : defaultNormalShiftValues,
                );
                setValue('startTime', '');
                setValue('endTime', '');
                setValue('startDate', '');
                setValue('endDate', '');
                setValue('shiftDate', '');
                setState({
                    startTime: '',
                    endTime: '',
                    startDate: '',
                    endDate: '',
                    shiftDate: '',
                });
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                dispatch(
                    ShiftSchedulerAction.GetShifts({
                        limit: 5,
                        page: 1,
                        tabKey: tabKey,
                    }),
                );
                return;
            },
            onError: (err) => {
                Utils.showMessage('error', err?.response?.data?.message);
            },
        },
    );

    const shiftSaveHandler = (data) => {
        console.log(data, 'newwwewewewewe');
         

        let { locationId } = data;
        console.log('Raw locationId value:', data);
        if (typeof locationId !== 'string' || !locationId.includes(',')) {
        console.warn('Invalid locationId format:', locationId);
        return;
    }
        let addressStr = locationId.split(',');
        let address = addressStr[1];
        let location = addressStr[0];
        let address_id = address.split(':');
        let location_id = location.split(':');
        data.addressId = address_id[1];
        data.locationId = location_id[1];

        if (drawerStates?.typeOfShift == 'normal') {
            if (!state?.startTime || !state?.endTime || !state?.shiftDate) {
                setValidations({
                    startTime: !state?.startTime
                        ? 'Start time is required'
                        : '',
                    endTime: !state?.endTime ? 'End time is required' : '',
                    shiftDate: !state?.shiftDate
                        ? 'Shift Date is required'
                        : '',
                    startDate: '',
                    endDate: '',
                });
                return;
            } else if (state?.startTime && state?.endTime && state?.shiftDate) {
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                normalShiftMutation.mutate(data);
            }
        } else {
            if (!state?.startTime || !state?.endTime || !state?.startDate) {
                setValidations({
                    startTime: !state?.startTime
                        ? 'Start time is required'
                        : '',
                    endTime: !state?.endTime ? 'End time is required' : '',
                    shiftDate: '',
                    startDate: !state?.startDate ? 'Date is required' : '',
                    endDate: '',
                });
                return;
            } else if (state?.startTime && state?.endTime && state?.startDate) {
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                openShiftMutation.mutate(data);
            }
        }
    };

    const shiftCloseHandler = () => {
        setSelectedProperty(null);
        setDrawerStates((prev) => ({ ...prev, shiftTypeDrawer: false }));
        reset(
            drawerStates.typeOfShift == 'open'
                ? defaultOpenShiftValues
                : defaultNormalShiftValues,
        );
        setState({
            startTime: '',
            endTime: '',
            startDate: '',
            endDate: '',
            shiftDate: '',
        });
        setValidations({
            startTime: '',
            endTime: '',
            shiftDate: '',
            startDate: '',
            endDate: '',
        });
    };

    const propertyApiHandler = async (value) => {
        // Clear dependent fields
        setValue('addressId', '');
        setValue('locationId', '');
        if (!value) {
            setSelectedProperty(null);
            return;
        }
        // Resolve selected property for address/location derivation
        const property = await resolveProperty(value);
        setSelectedProperty(property);
    };

    // Pre-fill fields from the source shift when converting to open shift via drag.
    // handleDrop in CustomCalendar spreads completeRecord directly into the first arg,
    // so field values live on openShiftDetail itself (not under .completeRecord).
    useEffect(() => {
        if (
            drawerStates.shiftTypeDrawer &&
            drawerStates.typeOfShift === 'open' &&
            openShiftDetail?.property?.id
        ) {
            if (openShiftDetail.numberOfAssignees) {
                setValue('numberOfAssignees', String(openShiftDetail.numberOfAssignees));
            }
            setValue('propertyId', openShiftDetail.property.id);
            // Pre-select property so address/location options populate. Seed the
            // cache with what the shift carries so the dropdown can label it
            // before the detail call resolves.
            propertyCacheRef.current[openShiftDetail.property.id] = {
                ...(propertyCacheRef.current[openShiftDetail.property.id] || {}),
                ...openShiftDetail.property,
            };
            setSelectedProperty(
                propertyCacheRef.current[openShiftDetail.property.id],
            );
            resolveProperty(openShiftDetail.property.id).then(
                (property) => property && setSelectedProperty(property),
            );

            if (openShiftDetail.eligibilitity?.id) {
                setValue('eligibilityId', openShiftDetail.eligibilitity.id);
            }
            if (openShiftDetail.startTime) {
                const t = dayjs(openShiftDetail.startTime, 'HH:mm');
                setState((prev) => ({ ...prev, startTime: t }));
                setValue('startTime', openShiftDetail.startTime);
            }
            if (openShiftDetail.endTime) {
                const t = dayjs(openShiftDetail.endTime, 'HH:mm');
                setState((prev) => ({ ...prev, endTime: t }));
                setValue('endTime', openShiftDetail.endTime);
            }
            if (openShiftDetail.notes) {
                setValue('notes', openShiftDetail.notes);
            }
            // Default repeat frequency since normal shifts have no recurrence
            setValue('recurringFrequency', 'daily');
        }
    }, [drawerStates.shiftTypeDrawer, drawerStates.typeOfShift]);

    return (
        <>
            <BasicDrawer
                openDrawer={drawerStates?.shiftTypeDrawer}
                width={500}
                title={
                    drawerStates?.typeOfShift == 'open'
                        ? 'Open Shift'
                        : 'Normal Shift'
                }
                onClose={() => shiftCloseHandler()}
                children={
                    <>
                        <form>
                            <Row gutter={[16]} className="mb-4">
                                {drawerStates?.typeOfShift == 'normal' ? (
                                    <Col lg={13} md={13} sm={24} xs={24}>
                                        <Controller
                                            name="assignTo"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <LabelWithSelect
                                                    options={currentUserOption} // Show only the current user
                                                    labeltext={'Assign To'}
                                                    field={field}
                                                    labelClass={'font-normal'}
                                                    inputClass={
                                                        'outline-none py-1 rounded h-9'
                                                    }
                                                />
                                            )}
                                        />
                                    </Col>
                                ) : (
                                    <Col lg={24} md={24} sm={24} xs={24}>
                                        <Controller
                                            name="numberOfAssignees"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <LabelWithField
                                                        labeltext={
                                                            'Number Of Shifts'
                                                        }
                                                        field={field}
                                                        labelClass={
                                                            'font-normal'
                                                        }
                                                        inputClass={
                                                            'outline-none py-1 rounded h-9'
                                                        }
                                                    />
                                                    {fieldState?.error && (
                                                        <p
                                                            style={{
                                                                color: Colors.Error,
                                                            }}
                                                        >
                                                            {
                                                                fieldState
                                                                    ?.error
                                                                    ?.message
                                                            }
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                )}

                                {/* <Col lg={11} md={11} sm={24} xs={24}>
                                    <Controller
                                        name="break"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <div className="">
                                                <LabelWithSelect
                                                    options={shiftBreaks}
                                                    labeltext={'Un-paid Break'}
                                                    field={field}
                                                    labelClass={'font-normal'}
                                                    inputClass={
                                                        'outline-none py-1 rounded h-9'
                                                    }
                                                />
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col> */}
                            </Row>

                            <Row gutter={[16, 32]} className="">
                                <Col lg={13} md={13} sm={24} xs={24}>
                                    <Controller
                                        name="startTime"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <>
                                                <div className="flex flex-col">
                                                    <label
                                                        style={{
                                                            color: Colors.LabelColor,
                                                        }}
                                                        className="font-normal"
                                                    >
                                                        Start Time
                                                    </label>
                                                    <ConfigProvider
                                                        theme={{
                                                            components: {
                                                                DatePicker: {
                                                                    optionSelectedBg:
                                                                        colorPrimary,
                                                                    optionActiveBg:
                                                                        Colors.LightGray,
                                                                    optionSelectedColor:
                                                                        Colors.White,
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <TimePicker
                                                            value={
                                                                state?.startTime
                                                                    ? state?.startTime
                                                                    : dayjs()
                                                            }
                                                            minuteStep={15}
                                                            format={[
                                                                'HH:mm',
                                                                'HHmm',
                                                            ]}
                                                            onChange={(
                                                                time,
                                                            ) => {
                                                                setState(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        startTime:
                                                                            time,
                                                                    }),
                                                                );
                                                                setValue(
                                                                    'startTime',
                                                                    time?.format(
                                                                        'HH:mm',
                                                                    ),
                                                                );
                                                            }}
                                                            className="outline-none py-1 rounded-md h-9"
                                                        />
                                                    </ConfigProvider>
                                                    {validation?.startTime && (
                                                        <p
                                                            style={{
                                                                color: Colors.Error,
                                                            }}
                                                        >
                                                            {
                                                                validation?.startTime
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col lg={11} md={11} sm={24} xs={24}>
                                    <Controller
                                        name="endTime"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <>
                                                <div className="flex flex-col">
                                                    <label
                                                        style={{
                                                            color: Colors.LabelColor,
                                                        }}
                                                        className="font-normal"
                                                    >
                                                        End Time
                                                    </label>
                                                    <ConfigProvider
                                                        theme={{
                                                            components: {
                                                                DatePicker: {
                                                                    optionSelectedBg:
                                                                        colorPrimary,
                                                                    optionActiveBg:
                                                                        Colors.LightGray,
                                                                    optionSelectedColor:
                                                                        Colors.White,
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <TimePicker
                                                            value={
                                                                state?.endTime
                                                                    ? state?.endTime
                                                                    : dayjs()
                                                            }
                                                            minuteStep={15}
                                                            format={[
                                                                'HH:mm',
                                                                'HHmm',
                                                            ]}
                                                            onChange={(
                                                                time,
                                                            ) => {
                                                                setState(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        endTime:
                                                                            time,
                                                                    }),
                                                                );
                                                                setValue(
                                                                    'endTime',
                                                                    time?.format(
                                                                        'HH:mm',
                                                                    ),
                                                                );
                                                            }}
                                                            className="outline-none py-1 rounded-md h-9"
                                                        />
                                                    </ConfigProvider>
                                                    {validation?.endTime && (
                                                        <p
                                                            style={{
                                                                color: Colors.Error,
                                                            }}
                                                        >
                                                            {
                                                                validation?.endTime
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row
                                gutter={[16]}
                                className="my-2 flex items-center"
                            >
                                <Col
                                    lg={13}
                                    md={13}
                                    sm={24}
                                    xs={24}
                                    className="mt-4"
                                >
                                    <Controller
                                        name="propertyId"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <>
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    Property Site
                                                </label>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Select: {
                                                                optionSelectedBg:
                                                                    colorPrimary,
                                                                optionActiveBg:
                                                                    Colors.LightGray,
                                                                optionSelectedColor:
                                                                    Colors.White,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <PaginatedSelect
                                                        allowClear={true}
                                                        fetchPage={fetchPropertiesPage}
                                                        showFullLabelToggle
                                                        enabled={
                                                            drawerStates.typeOfShift == 'normal' ||
                                                            drawerStates.typeOfShift == 'open'
                                                        }
                                                        extraOptions={selectedPropertyOption}
                                                        placeholder="Search property site"
                                                        className="w-full h-9 outline-none rounded"
                                                        value={value || undefined}
                                                        onChange={(val) => {
                                                            onChange(val);
                                                            propertyApiHandler(val);
                                                        }}
                                                    />
                                                </ConfigProvider>
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                ?.message
                                                        }
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col lg={11} md={11} sm={24} xs={24}>
                                    <Controller
                                        name="addressId"
                                        control={control}
                                        render={({ field: { value, onChange }, fieldState }) => (
                                            <>
                                                <label
                                                    className="font-normal"
                                                    style={{ color: Colors.LabelColor }}
                                                >
                                                    Address
                                                </label>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Select: {
                                                                optionSelectedBg: colorPrimary,
                                                                optionActiveBg: Colors.LightGray,
                                                                optionSelectedColor: Colors.White,
                                                                selectorBg: Colors.LightGray,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <PaginatedSelect
                                                        allowClear
                                                        items={addressOptions}
                                                        className="w-full h-9 outline-none rounded"
                                                        disabled={!selectedProperty}
                                                        placeholder={
                                                            selectedProperty
                                                                ? 'Search address'
                                                                : 'Select a Property Site first'
                                                        }
                                                        value={value || undefined}
                                                        onChange={onChange}
                                                    />
                                                </ConfigProvider>
                                                {fieldState?.error && (
                                                    <p style={{ color: Colors.Error }}>
                                                        {fieldState?.error?.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            {/* Address Field — populated from selectedProperty.addresses */}
                            <Row className="mb-4">
                                <Col lg={13} md={13} sm={24} xs={24}>

                                    <Controller
                                        name="eligibilityId"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <div className="">
                                                <LabelWithSelect
                                                    options={
                                                        elegibilityOptions
                                                            ? elegibilityOptions
                                                            : []
                                                    }
                                                    labeltext={'Position'}
                                                    field={field}
                                                    labelClass={'font-normal'}
                                                    inputClass={
                                                        'outline-none py-1 rounded h-9'
                                                    }
                                                />
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row
                                gutter={[8, 32]}
                                className="flex items-center justify-between w-full"
                                style={{}}
                            >
                                {drawerStates?.typeOfShift !== 'open' ? (
                                    <Col
                                        lg={13}
                                        md={13}
                                        sm={24}
                                        xs={24}
                                        className="w-full"
                                    >
                                        <label
                                            className="font-normal"
                                            style={{ color: Colors.LabelColor }}
                                        >
                                            Shift Date
                                        </label>
                                        <div
                                            className="w-full"
                                            style={{ width: '100%' }}
                                        >
                                            <Controller
                                                name="shiftDate"
                                                control={control}
                                                render={({
                                                    field: { value, onChange },
                                                    fieldState,
                                                }) => (
                                                    <>
                                                        <DatePicker
                                                            className="outline-none py-1 rounded-md h-9 w-full"
                                                            allowClear={true}
                                                            format={
                                                                'DD-MM-YYYY'
                                                            }
                                                            defaultValue={
                                                                state?.shiftDate
                                                                    ? dayjs(
                                                                        state?.shiftDate,
                                                                    )
                                                                    : dayjs()
                                                            }
                                                            onChange={(
                                                                date,
                                                                dateString,
                                                            ) => {
                                                                setState(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        shiftDate:
                                                                            dateString,
                                                                    }),
                                                                );
                                                                setValue(
                                                                    'shiftDate',
                                                                    dateString,
                                                                );
                                                            }}
                                                        />
                                                        {validation?.shiftDate && (
                                                            <p
                                                                style={{
                                                                    color: Colors.Error,
                                                                }}
                                                            >
                                                                {
                                                                    validation.shiftDate
                                                                }
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>
                                    </Col>
                                ) : (
                                    <>
                                        <Col lg={13} md={13} sm={24} xs={24}>
                                            <label
                                                className="font-normal"
                                                style={{
                                                    color: Colors.LabelColor,
                                                }}
                                            >
                                                Date Range
                                            </label>
                                            <div>
                                                <Controller
                                                    name="startDate"
                                                    control={control}
                                                    render={({
                                                        field: {
                                                            value,
                                                            onChange,
                                                        },
                                                        fieldState,
                                                    }) => (
                                                        <>
                                                            <RangeDatePicker
                                                                allowClear={
                                                                    true
                                                                }
                                                                format={
                                                                    'DD-MM-YYYY'
                                                                }
                                                                className={
                                                                    'outline-none py-1 rounded-md h-9 w-full'
                                                                }
                                                                defaultValue={
                                                                    state?.startDate
                                                                        ? [
                                                                            dayjs(
                                                                                state?.startDate,
                                                                            ),
                                                                            dayjs(
                                                                                state?.endDate,
                                                                            ),
                                                                        ]
                                                                        : [
                                                                            dayjs(),
                                                                            dayjs(),
                                                                        ]
                                                                }
                                                                onChange={(
                                                                    date,
                                                                    dateStrings,
                                                                ) => {
                                                                    setState(
                                                                        (
                                                                            prev,
                                                                        ) => ({
                                                                            ...prev,
                                                                            startDate:
                                                                                date &&
                                                                                date[0],
                                                                            endDate:
                                                                                date &&
                                                                                date[1],
                                                                        }),
                                                                    );
                                                                    setValue(
                                                                        'startDate',
                                                                        dateStrings &&
                                                                        dateStrings[0],
                                                                    );
                                                                    setValue(
                                                                        'endDate',
                                                                        dateStrings &&
                                                                        dateStrings[1],
                                                                    );
                                                                }}
                                                            />
                                                            {validation?.startDate && (
                                                                <p
                                                                    style={{
                                                                        color: Colors.Error,
                                                                    }}
                                                                >
                                                                    {
                                                                        validation?.startDate
                                                                    }
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </Col>
                                        <Col
                                            lg={11}
                                            md={11}
                                            sm={24}
                                            xs={24}
                                            className="w-full mb-2"
                                            style={{
                                                paddingRight: 0,
                                                paddingLeft: '4px',
                                            }}
                                        >
                                            <Controller
                                                name="recurringFrequency"
                                                control={control}
                                                render={({
                                                    field,
                                                    fieldState,
                                                }) => (
                                                    <div
                                                        className="w-full"
                                                        style={{
                                                            paddingRight: 0,
                                                            paddingLeft: '4px',
                                                        }}
                                                    >
                                                        <LabelWithSelect
                                                            options={[
                                                                {
                                                                    value: 'daily',
                                                                    label: 'Daily',
                                                                },
                                                                {
                                                                    value: 'weekly',
                                                                    label: 'Weekly',
                                                                },
                                                                {
                                                                    value: 'biweekly',
                                                                    label: 'Biweekly',
                                                                },
                                                                {
                                                                    value: 'monthly',
                                                                    label: 'Monthly',
                                                                },
                                                                {
                                                                    value: 'yearly',
                                                                    label: 'Yearly',
                                                                },
                                                            ]}
                                                            labeltext={
                                                                'Repeat Shift'
                                                            }
                                                            field={field}
                                                            labelClass={
                                                                'font-normal'
                                                            }
                                                            inputClass={
                                                                'outline-none py-1 rounded h-9'
                                                            }
                                                            style={{
                                                                paddingTop: 0,
                                                                paddingBottom: 0,
                                                            }}
                                                        />
                                                        {fieldState?.error && (
                                                            <p
                                                                style={{
                                                                    color: Colors.Error,
                                                                }}
                                                            >
                                                                {
                                                                    fieldState
                                                                        ?.error
                                                                        .message
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </Col>
                                    </>
                                )}
                            </Row>

                            <Row className="mb-4">
                                <Col lg={13} md={11} sm={24} xs={24}>
                                    <Controller
                                        name="locationId"
                                        control={control}
                                        render={({ field: { value, onChange }, fieldState }) => (
                                            <>
                                                <label
                                                    className="font-normal"
                                                    style={{ color: Colors.LabelColor }}
                                                >
                                                    Location
                                                </label>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Select: {
                                                                optionSelectedBg: colorPrimary,
                                                                optionActiveBg: Colors.LightGray,
                                                                optionSelectedColor: Colors.White,
                                                                selectorBg: Colors.LightGray,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <PaginatedSelect
                                                        allowClear
                                                        items={locationOptions}
                                                        className="w-full h-9 outline-none rounded"
                                                        disabled={!selectedProperty}
                                                        placeholder={
                                                            selectedProperty
                                                                ? 'Search location'
                                                                : 'Select a Property Site first'
                                                        }
                                                        value={value || undefined}
                                                        onChange={onChange}
                                                    />
                                                </ConfigProvider>
                                                {fieldState?.error && (
                                                    <p style={{ color: Colors.Error }}>
                                                        {fieldState?.error?.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row gutter={[16, 32]}>
                                <Col span={24}>
                                    <Controller
                                        name="notes"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <div className="">
                                                <TextField.Textarea
                                                    style={{
                                                        color: Colors.LightGray,
                                                    }}
                                                    placeholder={''}
                                                    value={value}
                                                    onChange={onChange}
                                                    labeltext={'Note'}
                                                    className={
                                                        'outline-none h-10 rounded'
                                                    }
                                                />
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row gutter={[16, 32]} className="my-6">
                                <Col
                                    span={24}
                                    className="flex items-center justify-end"
                                >
                                    <Button.Basic
                                        onClick={handleSubmit(shiftSaveHandler)}
                                        htmlType="submit"
                                        loader={
                                            normalShiftMutation?.isLoading ||
                                            openShiftMutation.isLoading
                                        }
                                        size={'large'}
                                        text={'Save'}
                                        buttonStyle={{
                                            backgroundColor: colorPrimary,
                                            color: 'white',
                                        }}
                                        className={'px-12 rounded-md h-12'}
                                    />
                                </Col>
                            </Row>
                        </form>
                    </>
                }
            />
        </>
    );
};

export default AssignShiftDrawer;
